<template>
  <div
    class="interval-column"
    :style="columnStyle"
  >
    <calendar-entry
      v-for="terminEntryDTO in dayDTO.time_block_list"
      :key="terminEntryDTO.id"
      :style="timeBlockStyles[terminEntryDTO.id]"
      :termin-entry-d-t-o="terminEntryDTO"
      :day-conflicts="dayDTO.conflicts"
    ></calendar-entry>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import CalendarEntry from '@/features/visual_planner/components/CalendarEntry.vue'
  import dateUtils from '@/features/visual_planner/mixins/DateUtils'

  export default {
    name: 'DayColumn',

    mixins: [dateUtils],

    components: {
      CalendarEntry,
    },

    props: {
      index: {
        type: Number,
        required: true,
      },
      date: {
        type: Date,
        required: true,
      },
      layoutDayInterval: {
        type: Number,
        default: null,
      },
    },

    data() {
      return {
        timeBlockStyles: {},
      }
    },

    computed: {
      ...mapState('VisualPlannerMetadata', ['dayInterval']),
      ...mapState('VisualPlannerData', ['dayList']),

      columnStyle() {
        const total = this.layoutDayInterval || this.dayInterval
        return {
          left: `${(100 / total) * this.index}%`,
          width: `${100 / total}%`,
        }
      },

      dayDTO() {
        return this.dayList.find(
          (day) => day.day_metadata === this.getSQLDateFromDate(this.date)
        )
      },
    },

    watch: {
      dayDTO: {
        handler(newDayDTO) {
          if (!newDayDTO) return
          this.$nextTick(() => this.layoutTimeBlocks(newDayDTO.time_block_list))
        },
        immediate: true,
        deep: true,
      },
    },

    methods: {
      layoutTimeBlocks(timeBlockList) {
        const events = timeBlockList.map((block) => {
          const [startHour, startMinute] = block.von_uhrzeit
            .split(':')
            .map(Number)
          const [endHour, endMinute] = block.bis_uhrzeit.split(':').map(Number)
          const start = startHour * 60 + startMinute
          const end = endHour * 60 + endMinute

          return {
            id: block.id,
            top: start,
            bottom: end,
            height: end - start,
          }
        })

        events.sort((e1, e2) => {
          if (e1.top < e2.top) return -1
          if (e1.top > e2.top) return 1
          if (e1.bottom < e2.bottom) return -1
          if (e1.bottom > e2.bottom) return 1
          return 0
        })

        const columns = []
        let lastEventEnding = null

        events.forEach((event) => {
          if (lastEventEnding !== null && event.top >= lastEventEnding) {
            this.packEvents(columns)
            columns.length = 0
            lastEventEnding = null
          }

          let placed = false
          for (let i = 0; i < columns.length; i++) {
            const col = columns[i]
            if (!this.collidesWith(col[col.length - 1], event)) {
              col.push(event)
              placed = true
              break
            }
          }

          if (!placed) {
            columns.push([event])
          }

          if (lastEventEnding === null || event.bottom > lastEventEnding) {
            lastEventEnding = event.bottom
          }
        })

        if (columns.length > 0) {
          this.packEvents(columns)
        }
      },

      packEvents(columns) {
        const n = columns.length

        columns.forEach((col, i) => {
          col.forEach((event) => {
            const colSpan = this.expandEvent(event, i, columns)
            const isLastColumn = i + colSpan === n

            this.$set(this.timeBlockStyles, event.id, {
              left: `${(i / n) * 100}%`,
              width: `calc(${(100 * colSpan) / n}% ${
                isLastColumn ? '- 10px' : ''
              })`,
              top: `${(event.top / (24 * 60)) * 100}%`,
              height: `${(event.height / (24 * 60)) * 100}%`,
            })
          })
        })
      },
      collidesWith(a, b) {
        return a.bottom > b.top && a.top < b.bottom
      },

      expandEvent(ev, iColumn, columns) {
        let colSpan = 1

        for (let i = iColumn + 1; i < columns.length; i++) {
          const col = columns[i]
          let canExpand = true

          for (let j = 0; j < col.length; j++) {
            const ev1 = col[j]
            if (this.collidesWith(ev, ev1)) {
              return colSpan
            }
          }
          colSpan++
        }
        return colSpan
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import '@/features/visual_planner/scss/color';

  .interval-column {
    position: absolute;
    height: 100%;
    border-left: 1px solid $color-border-primary;
  }
</style>
